import { MarketplaceSchedule } from '@spike/model';
import clsx from 'clsx';
import { DatePicker } from 'components/UI/V3/DatePicker/DatePicker';
import { getWaitlistTimeSlots, WaitlistFormValues } from 'components/Waitlist/WaitlistForm/waitlistFormUtils';
import { formatPreferredTime, formatWaitlistDate } from 'components/Waitlist/waitlistUtils';
import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useMemo, useState } from 'react';
import calendarStyles from './WaitlistCalendar.module.scss';
import styles from './WaitlistPreferencesFields.module.scss';
import WaitlistPickerField from './WaitlistPickerField';

type Preferences = Pick<WaitlistFormValues, 'preferredTime' | 'validUntil'>;

interface WaitlistPreferencesFieldsProps extends Preferences {
    /** Earliest date asked for in the services; null when they are all ASAP. */
    firstDate: Moment | null;
    timeZone: string;
    schedule?: MarketplaceSchedule;
    onChange: (changes: Partial<Preferences>) => void;
}

const formatDate = (date: Moment) => formatWaitlistDate(date.format('YYYY-MM-DD'));

// Preferred Time and Valid Until. The requested dates belong to each service ("Pets & Services").
export const WaitlistPreferencesFields: FunctionComponent<WaitlistPreferencesFieldsProps> = ({
    firstDate,
    preferredTime,
    validUntil,
    timeZone,
    schedule,
    onChange
}) => {
    // Active option of each popover: the "no preference" one or the picker.
    const [timeOption, setTimeOption] = useState(preferredTime ? 'time' : 'anytime');
    const [validUntilOption, setValidUntilOption] = useState(validUntil ? 'date' : 'ongoing');

    const today = moment().tz(timeZone).startOf('day');

    // Hours of the first requested date, or of the whole week when everything is ASAP.
    const timeSlots = useMemo(
        () => getWaitlistTimeSlots(schedule, firstDate),
        [schedule, firstDate?.format('YYYY-MM-DD')]
    );

    const validUntilMin = firstDate && firstDate.isAfter(today) ? firstDate : today;

    return (
        <div className={styles.root}>
            <WaitlistPickerField
                label="Preferred Time"
                variant="list"
                value={formatPreferredTime(preferredTime)}
                options={[
                    { id: 'anytime', label: 'Anytime' },
                    { id: 'time', label: 'Select Time' }
                ]}
                activeOption={timeOption}
                onSelectOption={(option, close) => {
                    setTimeOption(option);

                    if (option === 'anytime') {
                        onChange({ preferredTime: null });
                        close();
                    }
                }}
                renderPicker={close =>
                    timeSlots.length === 0 ? (
                        <p className={styles.muted}>The location is closed on the first requested date.</p>
                    ) : (
                        <div className={styles.times}>
                            {timeSlots.map(slot => (
                                <button
                                    key={slot}
                                    type="button"
                                    aria-pressed={slot === preferredTime}
                                    className={clsx(styles.time, { [styles.timeSelected]: slot === preferredTime })}
                                    onClick={() => {
                                        onChange({ preferredTime: slot });
                                        close();
                                    }}
                                >
                                    {formatPreferredTime(slot)}
                                </button>
                            ))}
                        </div>
                    )
                }
            />

            <WaitlistPickerField
                label="Valid Until"
                align="right"
                value={validUntil ? formatDate(validUntil) : 'Ongoing'}
                options={[
                    { id: 'ongoing', label: 'Ongoing' },
                    { id: 'date', label: 'Select Date' }
                ]}
                activeOption={validUntilOption}
                onSelectOption={(option, close) => {
                    setValidUntilOption(option);

                    if (option === 'ongoing') {
                        onChange({ validUntil: null });
                        close();
                    }
                }}
                renderPicker={close => (
                    <DatePicker
                        className={calendarStyles.calendar}
                        timeZone={timeZone}
                        minDate={validUntilMin}
                        value={validUntil}
                        onChange={(date: Moment | null) => {
                            onChange({ validUntil: date });
                            date && close();
                        }}
                    />
                )}
            />
        </div>
    );
};

export default WaitlistPreferencesFields;
